import * as React from 'react';
import * as Sentry from '@sentry/react';
import { useRoutes } from 'react-router-dom';

const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.breadcrumbsIntegration({ dom: false }),
      Sentry.browserTracingIntegration(),
    ],
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    beforeBreadcrumb: (breadcrumb) => {
      if (!breadcrumb) return null;

      const category =
        breadcrumb.category === 'navigation'
          ? 'navigation'
          : ['fetch', 'xhr', 'http'].includes(breadcrumb.category ?? '')
            ? 'network'
            : breadcrumb.category?.startsWith('ui.')
              ? 'ui.interaction'
              : 'app';
      const data: Record<string, string | number> = {};

      if (category === 'ui.interaction') {
        const element = breadcrumb.data?.element;
        if (typeof element === 'string') data.element = element;
      } else if (category === 'network') {
        const method = breadcrumb.data?.method;
        const statusCode = breadcrumb.data?.status_code;
        if (typeof method === 'string') data.method = method;
        if (typeof statusCode === 'number') data.status_code = statusCode;
      }

      return {
        timestamp: breadcrumb.timestamp,
        category,
        type: breadcrumb.type,
        level: breadcrumb.level,
        message:
          category === 'navigation'
            ? 'Route changed'
            : category === 'ui.interaction'
              ? 'UI element clicked'
              : undefined,
        data: Object.keys(data).length ? data : undefined,
      };
    },
    beforeSendSpan: (span) => ({
      ...span,
      attributes: Object.fromEntries(
        Object.entries(span.attributes).filter(
          ([key]) => !/(url|query|user|cookie|authorization|request\.body|response\.body)/i.test(key),
        ),
      ),
    }),
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      Sentry.addBreadcrumb({
        category: 'ui.click',
        message: 'UI element clicked',
        data: { element: event.target.tagName.toLowerCase() },
      });
    },
    true,
  );
}

export const useSentryRoutes = sentryDsn
  ? Sentry.wrapUseRoutesV6(useRoutes)
  : useRoutes;

export const SentryErrorBoundary = sentryDsn
  ? Sentry.ErrorBoundary
  : React.Fragment;
